import { NavLink, Outlet, useNavigate } from "react-router-dom";
import Footer from "./Footer";
import {
  LayoutDashboard,
  CarFront,
  PackagePlus,
  KeyRound,
  ScrollText,
  BarChart3,
  Receipt,
  Users,
  Building2,
  Sparkles,
  LogOut,
} from "lucide-react";
import { useAuth } from "../lib/auth";

const NAV = [
  { to: "/ozet", label: "Özet", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/stok", label: "Stok", icon: CarFront, testid: "nav-inventory" },
  { to: "/arac-ekle", label: "Araç Ekle", icon: PackagePlus, testid: "nav-add" },
  { to: "/kiralama", label: "Kiralama", icon: KeyRound, testid: "nav-rentals" },
  { to: "/satis-gecmisi", label: "Satışlar", icon: ScrollText, testid: "nav-sales" },
  { to: "/analiz", label: "Analiz", icon: BarChart3, testid: "nav-analytics" },
  { to: "/giderler", label: "Giderler", icon: Receipt, testid: "nav-expenses" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    ...NAV,
    ...(user?.role === "admin"
      ? [
          { to: "/uyeler", label: "Üyeler", icon: Users, testid: "nav-members" },
          { to: "/abonelik", label: "Abonelik", icon: Sparkles, testid: "nav-subscription" },
        ]
      : []),
    ...(user?.is_portal_owner
      ? [{ to: "/portal", label: "Portal", icon: Building2, testid: "nav-portal" }]
      : []),
  ];

  const handleLogout = () => {
    logout();
    navigate("/giris");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-stone-200 bg-white/70 backdrop-blur-xl z-30">
        <div className="px-6 pt-8 pb-6 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-slate-900 flex items-center justify-center">
              <CarFront className="w-5 h-5 text-orange-400" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="font-display font-black text-slate-900 leading-none truncate">
                {user?.company_name || "Oto Galeri"}
              </div>
              <div className="text-[11px] text-stone-500 tracking-widest uppercase mt-1">
                Yönetim Paneli
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive
                    ? "bg-stone-100 text-slate-900 font-semibold"
                    : "text-stone-600 hover:bg-stone-50 hover:text-slate-900"
                }`
              }
            >
              <n.icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-stone-200">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-semibold text-slate-900 truncate">
              {user?.name}
            </div>
            <div className="text-xs text-stone-500 truncate">
              {user?.email} · {user?.role === "admin" ? "Yönetici" : "Üye"}
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm text-stone-600 hover:bg-stone-100 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-stone-200">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center">
              <CarFront className="w-4 h-4 text-orange-400" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="font-display font-black text-slate-900 text-sm leading-none truncate max-w-[180px]">
                {user?.company_name || "Oto Galeri"}
              </div>
              <div className="text-[10px] text-stone-500 tracking-widest uppercase mt-0.5">
                Yönetim
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn-mobile"
            className="p-2 rounded-lg text-stone-600 hover:bg-stone-100"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="md:pl-64 pb-24 md:pb-10 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
          <Outlet />
        </div>
        <div className="md:pl-0">
          <Footer />
        </div>
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-stone-200 no-print">
        <div className="flex overflow-x-auto no-scrollbar px-2 py-1.5">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`${n.testid}-mobile`}
              className={({ isActive }) =>
                `flex-1 min-w-[70px] flex flex-col items-center gap-1 px-2 py-2 rounded-lg text-[11px] font-medium transition-colors ${
                  isActive ? "text-orange-700" : "text-stone-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <n.icon
                    className="w-5 h-5"
                    strokeWidth={isActive ? 2.2 : 1.7}
                  />
                  <span>{n.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
